var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import AdminPageLayout from '../../../layout/admin-page-layout';
import { useFetch } from '@rad/hooks';
import Input from '@rad/components/inputs/input';
import CheckSwitch from '@rad/components/inputs/checkswitch';
import Modal from '@rad/components/ui/modal';
import createTable from '@rad/components/ui/table';
import ActionButton from '@rad/components/ui/action-button';
import { expand } from '@rad/utility/validation';
import { getDiscussionKinds, createDiscussionKind, updateDiscussionKind, deleteDiscussionKind } from '../../../common/network/discussion-kind';
import { createDiscussionKindState } from '../../../types/discussion-kind';
import { useToast } from '../../../contexts/toast';
function DiscussionKinds() {
    const toast = useToast();
    const [kindsPromise, reloadKinds] = useFetch(getDiscussionKinds());
    const [showModal, setShowModal] = React.useState(false);
    const [editingName, setEditingName] = React.useState(null);
    const [form, setForm] = React.useState(createDiscussionKindState());
    const [errors, setErrors] = React.useState({});
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [deletingName, setDeletingName] = React.useState(null);
    const KindsTable = React.useMemo(() => createTable(kindsPromise), [kindsPromise]);
    const handleAdd = () => {
        setEditingName(null);
        setForm(createDiscussionKindState());
        setErrors({});
        setShowModal(true);
    };
    const handleEdit = (kind) => {
        setEditingName(kind.name);
        setForm({
            name: kind.name,
            display: kind.display,
            is_public: kind.isPublic,
        });
        setErrors({});
        setShowModal(true);
    };
    const handleSave = () => __awaiter(this, void 0, void 0, function* () {
        setErrors({});
        const [success, err] = editingName
            ? yield updateDiscussionKind(editingName, { display: form.display, is_public: form.is_public })
            : yield createDiscussionKind(form);
        if (success) {
            setShowModal(false);
            reloadKinds();
            toast.success(editingName ? 'Discussion kind updated' : 'Discussion kind created');
        }
        else {
            const [, expanded] = expand(err);
            setErrors(expanded);
            toast.error('Please fix the errors below');
        }
    });
    const handleDeleteClick = (name) => {
        setDeletingName(name);
        setShowDeleteModal(true);
    };
    const handleDeleteConfirm = () => __awaiter(this, void 0, void 0, function* () {
        if (!deletingName)
            return;
        yield deleteDiscussionKind(deletingName);
        setShowDeleteModal(false);
        setDeletingName(null);
        reloadKinds();
        toast.success('Discussion kind deleted');
    });
    return (_jsx(AdminPageLayout, { children: _jsxs("div", { className: "container py-4", children: [_jsx("h5", { className: "fw-semibold mb-3", children: "Discussion Forums" }), _jsx("div", { className: "d-flex justify-content-between align-items-center mb-4", children: _jsxs("button", { className: "btn btn-primary", onClick: handleAdd, children: [_jsx("i", { className: "bi bi-plus-lg me-2" }), "Add Discussion Kind"] }) }), _jsxs(KindsTable, { className: "mb-0", children: [_jsx(KindsTable.Column, { header: "Name", accessor: "name" }), _jsx(KindsTable.Column, { header: "Display", accessor: "display" }), _jsx(KindsTable.Column, { header: "Public", render: dk => (_jsx("span", { className: `badge ${dk.isPublic ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`, children: dk.isPublic ? 'Public' : 'Private' })) }), _jsx(KindsTable.Column, { header: "", className: "text-end", render: dk => (_jsxs(_Fragment, { children: [_jsx("button", { className: "btn btn-link text-primary p-0 me-2", onClick: () => handleEdit(dk), title: "Edit", children: _jsx("i", { className: "bi bi-pencil" }) }), _jsx("button", { className: "btn btn-link text-danger p-0", onClick: () => handleDeleteClick(dk.name), title: "Delete", children: _jsx("i", { className: "bi bi-trash" }) })] })) })] }), _jsx(Modal, { isOpen: showModal, title: editingName ? 'Edit Discussion Kind' : 'Add Discussion Kind', onClose: () => setShowModal(false), onSave: handleSave, children: _jsxs("div", { className: "d-flex flex-column gap-3", children: [_jsx(Input, { kind: "text", label: "Name", value: form.name, onChange: v => setForm(Object.assign(Object.assign({}, form), { name: v })), invalid: errors.name, disabled: !!editingName }), _jsx(Input, { kind: "text", label: "Display Name", value: form.display, onChange: v => setForm(Object.assign(Object.assign({}, form), { display: v })), invalid: errors.display }), _jsx(CheckSwitch, { switch: true, label: "Public (visible to all visitors)", value: form.is_public, onChange: v => setForm(Object.assign(Object.assign({}, form), { is_public: v })) }), errors.form && _jsx("div", { className: "alert alert-danger", children: errors.form.join(', ') })] }) }), _jsx(Modal, { isOpen: showDeleteModal, title: "Delete Discussion Kind", onClose: () => setShowDeleteModal(false), footer: _jsx(ActionButton, { className: "btn btn-danger", onClick: handleDeleteConfirm, loadingText: "Deleting...", children: "Delete" }), children: _jsx("p", { children: "Are you sure you want to delete this discussion kind? This action cannot be undone." }) })] }) }));
}
export default DiscussionKinds;
